import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import TuningAdminTable, { TuningField, boolChip, safePreview } from './TuningAdminTable';

const depthOptions = ['high_level', 'medium', 'detailed'].map((v) => ({
  label: v,
  value: v,
}));

const fields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'display_name', label: 'Display Name', required: true },
  { name: 'section_title', label: 'Section Title' },
  {
    name: 'prompt_template',
    label: 'Prompt Template',
    type: 'textarea',
    required: true,
    minRows: 8,
    helperText: 'Supports {entity_type}, {entity_label}, {entity_name}, {context_name}.',
  },
  { name: 'purpose', label: 'Purpose', type: 'textarea', minRows: 4 },
  { name: 'content_focus', label: 'Content Focus', type: 'textarea', minRows: 8 },
  { name: 'content_boundaries', label: 'Content Boundaries', type: 'textarea', minRows: 8 },
  { name: 'default_depth_key', label: 'Default Depth', type: 'select', options: depthOptions },
  { name: 'requires_procedural_markdown', label: 'Procedural Markdown', type: 'boolean' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'is_active', label: 'Active', type: 'boolean' },
];

const ResourceTypesPanel: React.FC = () => {
  return (
    <TuningAdminTable
      title="Resource / Section Type Specs"
      description="Controls resource prompts, section purpose, content focus, boundaries, and default depth."
      endpoint="db/ai-resource-type-spec/"
      queryKey={['ai-resource-type-spec']}
      fields={fields}
      defaultValues={{
        key: '',
        display_name: '',
        section_title: '',
        prompt_template: '',
        purpose: '',
        content_focus: '',
        content_boundaries: '',
        default_depth_key: 'medium',
        requires_procedural_markdown: false,
        sort_order: 100,
        is_active: true,
      }}
      orderBy="sort_order"
      rowTitle={(row) => row.display_name || row.key}
      rowSubtitle={(row) => safePreview(row.prompt_template, 180)}
      columns={[
        {
          key: 'key',
          label: 'Resource',
          width: 230,
          render: (row) => (
            <Stack spacing={0.25}>
              <Typography variant="body2" fontWeight={700}>
                {row.display_name || row.key}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.key} / {row.section_title || '-'}
              </Typography>
            </Stack>
          ),
        },
        {
          key: 'prompt_template',
          label: 'Prompt',
          render: (row) => (
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
              {safePreview(row.prompt_template, 260)}
            </Typography>
          ),
        },
        {
          key: 'depth',
          label: 'Depth',
          width: 120,
          render: (row) => <Chip size="small" variant="outlined" label={row.default_depth_key || '-'} />,
        },
        {
          key: 'procedural',
          label: 'Procedural',
          width: 120,
          render: (row) => boolChip(row.requires_procedural_markdown),
        },
        {
          key: 'sort_order',
          label: 'Order',
          width: 90,
          render: (row) => row.sort_order,
        },
        {
          key: 'is_active',
          label: 'Active',
          width: 100,
          render: (row) => boolChip(row.is_active),
        },
      ]}
    />
  );
};

export default ResourceTypesPanel;